import { createSimpleContext } from "@mux/ui/context"
import { createMemo, createSignal, onCleanup } from "solid-js"
import { controlApiUrl } from "@/transport/control-api"
import { getAuthenticatedUser, logoutAuthenticatedUser, signInAsTestUser, type AuthUser } from "@/transport/auth"
import { createOAuthVerifierChallenge, readUserSessionToken, writeUserSessionToken } from "@/storage/user-session"
import { useAppInfo } from "@/context/app-info"
import { clearProvidersCache } from "@/hooks/use-providers"
import { clearToolsCache } from "@/hooks/use-tools"
import { clearProviderUsageCache } from "@/hooks/use-provider-usage"
import { clearFleetSnapshot, seedFleetSnapshot } from "@/utils/fleet-cache"
import { clearModelPreferencesSnapshot } from "@/context/model-preference-cache"
import {
  clearPendingPushDetach,
  currentPushSubscription,
  queuePendingPushDetach,
  unsubscribePushDeviceLocally,
} from "@/context/push-device"
import { createAuthSession } from "@/context/auth-session"
import { subscribeUserSession } from "@/storage/user-session"
import { readVerifiedUser, writeVerifiedUser } from "@/storage/verified-user"
import { claimDeviceState, deviceStateOwner } from "@/storage/workspace-owner"
export type { AuthUser } from "@/transport/auth"

// A device that still holds a session opens as the account it last verified,
// whose stored state it holds, while the control plane confirms it.
function rememberedUser(accountAuth: boolean) {
  if (!accountAuth || !readUserSessionToken()) return undefined
  const user = readVerifiedUser()
  return user && deviceStateOwner() === user.id ? user : undefined
}

async function fetchMe(accountAuth: boolean, signal: AbortSignal, shown: () => AuthUser | null): Promise<AuthUser | null> {
  if (!accountAuth) return null
  const machine = location.pathname.match(/\/dashboard\/m\/([^/]+)/)?.[1]
  const path = machine
    ? `/api/auth/me?machine=${encodeURIComponent(decodeURIComponent(machine))}`
    : "/api/auth/me"
  const data = await getAuthenticatedUser(path, { confirmUnauthorized: true, signal })
  signal.throwIfAborted()
  const user = data?.user ?? null
  if (user) claimDeviceState(user.id)
  writeVerifiedUser(user ?? undefined)
  // The page shows another account and has read its stored state: start over as this one.
  const showing = shown()
  if (user && showing && showing.id !== user.id) {
    location.reload()
    return new Promise<never>(() => {})
  }
  if (data?.machines) seedFleetSnapshot(data.machines)
  return user
}

export const { use: useAuth, provider: AuthProvider } = createSimpleContext({
  name: "Auth",
  gate: false,
  init: () => {
    const appInfo = useAppInfo()
    const [token, setToken] = createSignal(readUserSessionToken())
    const session = createAuthSession({
      remembered: rememberedUser(appInfo.accountAuth),
      check: (signal) => fetchMe(appInfo.accountAuth, signal, () => session.user()),
    })
    const currentUser = session.user
    let loggingOut = false
    const refresh = () => loggingOut ? Promise.resolve() : session.refresh()
    const recover = () => {
      if (document.visibilityState === "visible" && session.status() === "unavailable") void refresh()
    }
    window.addEventListener("online", recover)
    document.addEventListener("visibilitychange", recover)
    const unsubscribe = subscribeUserSession(() => {
      session.reset()
      setToken(readUserSessionToken())
      clearFleetSnapshot()
      clearModelPreferencesSnapshot()
      clearProvidersCache()
      clearToolsCache()
      clearProviderUsageCache()
      void refresh()
    })
    onCleanup(() => {
      unsubscribe()
      window.removeEventListener("online", recover)
      document.removeEventListener("visibilitychange", recover)
    })
    void refresh()
    const logout = async () => {
      const owner = currentUser()?.id ?? ""
      loggingOut = true
      session.reset()
      const pushSubscription = await currentPushSubscription().catch(() => null)
      if (pushSubscription) queuePendingPushDetach(owner, pushSubscription.endpoint)
      const detached = await logoutAuthenticatedUser(pushSubscription?.endpoint ?? "")
      if (detached && pushSubscription) clearPendingPushDetach(owner, pushSubscription.endpoint)
      await unsubscribePushDeviceLocally(pushSubscription, detached ? "" : owner).catch(() => undefined)
      writeUserSessionToken("")
      writeVerifiedUser(undefined)
      clearProvidersCache()
      clearToolsCache()
      clearProviderUsageCache()
      clearFleetSnapshot()
      clearModelPreferencesSnapshot()
      setToken("")
      loggingOut = false
    }
    const ready = () => session.status() !== "checking"

    return {
      status: session.status,
      user: currentUser,
      token,
      ready,
      authenticated: createMemo(() => !!currentUser()),
      async signInWithGoogle(returnTo = location.pathname + location.search) {
        const challenge = await createOAuthVerifierChallenge()
        const query = new URLSearchParams({ return_to: returnTo, verifier_challenge: challenge })
        location.assign(controlApiUrl(`/api/auth/google/start?${query.toString()}`))
      },
      /** Only when app info offers test sign-in: a new, empty account each time. */
      async signInAsTestUser(returnTo = "/dashboard/home") {
        await signInAsTestUser()
        location.assign(returnTo)
      },
      logout,
      refresh,
    }
  },
})
